import { Box, Button, Heading } from '@workwhile/ui'
import { getShifts } from 'api/shifts'
import { WorkShift } from 'api/typings'
import { format, parseISO, subDays } from 'date-fns'
import { useCompaniesForFiltersQuery } from 'queries/companies'
import React, { useEffect, useState } from 'react'
import BootstrapTable from 'react-bootstrap-table-next'
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css'
import { useSearchParams } from 'react-router-dom'
import ShiftsFilterBar2 from '../Shared/ShiftsFilterBar2'
import Loading from '../Shared/loading'
import { filterShiftsLocally, getRowStyles } from '../utils/util'
import { getShiftColumns } from './shifts'

const PastShifts = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const [filters, setFilters] = useState({
    companyIds: searchParams.get('companyIds')
      ? searchParams.get('companyIds')?.split(',').map(Number) ?? []
      : [],
    shiftId: searchParams.get('shiftId')
      ? Number(searchParams.get('shiftId'))
      : null,
    fromDate: searchParams.get('fromDate')
      ? format(parseISO(searchParams.get('fromDate') as string), 'yyyy-MM-dd')
      : format(subDays(new Date(), 2), 'yyyy-MM-dd'),
    toDate: searchParams.get('toDate')
      ? format(parseISO(searchParams.get('toDate') as string), 'yyyy-MM-dd')
      : format(new Date(), 'yyyy-MM-dd'),
    market: searchParams.get('market'),
    showUnfilledShiftsOnly: false,
    showCancelledShiftsOnly: false,
  })

  const [fetching, setFetching] = useState(false)
  const [shifts, setShifts] = useState<WorkShift[]>([])
  const columns = getShiftColumns('ongoing', null)
  const [needToFetchOnFirstLoad, setNeedToFetchOnFirstLoad] = useState(
    Array.from(searchParams).length > 0
  )
  const { data: companyOptions = [], isLoading: isCompaniesForFiltersLoading } =
    useCompaniesForFiltersQuery({ grouping: 'past' })

  useEffect(() => {
    const params = new URLSearchParams()
    if (filters.companyIds.length > 0) {
      params.set('companyIds', filters.companyIds.join(','))
    }
    if (filters.shiftId) {
      params.set('shiftId', String(filters.shiftId))
    }
    if (filters.market) {
      params.set('market', String(filters.market))
    }
    if (filters.fromDate) {
      params.set('fromDate', filters.fromDate)
    }
    if (filters.toDate) {
      params.set('toDate', filters.toDate)
    }
    setSearchParams(params)
    if (needToFetchOnFirstLoad) {
      fetchShifts()
      setNeedToFetchOnFirstLoad(false)
    }
  }, [filters])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFilters((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target
    setFilters((prevState) => ({
      ...prevState,
      [name]: checked,
    }))
  }

  const fetchShifts = () => {
    setFetching(true)
    const fromDateToUse =
      filters.fromDate || format(subDays(new Date(), 2), 'yyyy-MM-dd')
    const toDateToUse = filters.toDate || format(new Date(), 'yyyy-MM-dd')
    getShifts({
      type: 'past',
      startDate: toDateToUse,
      endDate: fromDateToUse,
      companyIds: filters.companyIds,
      shiftId: filters.shiftId ?? undefined,
      market: filters.market ?? undefined,
      isUnfilled: filters.showUnfilledShiftsOnly,
    })
      .then((shiftsData) => {
        setShifts(shiftsData)
      })
      .finally(() => setFetching(false))
  }

  return (
    <Box>
      <Box
        as="form"
        mb={4}
        onSubmit={(e) => {
          e.preventDefault()
          fetchShifts()
        }}
      >
        <Heading level={3}>Past shifts</Heading>
        {Boolean(isCompaniesForFiltersLoading) && <Loading />}
        {!isCompaniesForFiltersLoading && (
          <ShiftsFilterBar2
            {...(filters as any)}
            companyOptions={companyOptions}
            handleInputChange={handleInputChange}
            handleCheckboxChange={handleCheckboxChange}
          />
        )}
        <Box my={3}>Note: By default the dates filter for the past 2 days</Box>
        <Button type="submit" disabled={fetching}>
          Load shifts
        </Button>
        {Boolean(fetching) && <Loading />}
      </Box>
      {Boolean(shifts && shifts.length > 0 && !fetching) && (
        <BootstrapTable
          striped
          hover
          keyField="id"
          data={filterShiftsLocally(
            shifts,
            filters.showCancelledShiftsOnly,
            filters.showUnfilledShiftsOnly
          )}
          columns={columns}
          rowStyle={getRowStyles}
        />
      )}
    </Box>
  )
}

export default PastShifts
